import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Calendar,
  FileText,
  Receipt,
  Video,
  MessagesSquare,
  ShieldCheck,
  Clock,
  Zap,
  CheckCircle2,
  Wallet,
  ScanLine,
  ClipboardCheck,
  UserCheck,
} from "lucide-react";
import { LINKS } from "@/config/links";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const stagger = {
  show: { transition: { staggerChildren: 0.08 } },
};

const services = [
  { icon: FileText, title: "Monthly Bookkeeping", body: "Clean books, every month — categorized, reconciled, and reviewed in QuickBooks Online." },
  { icon: Wallet, title: "Bank & Card Reconciliations", body: "Every account tied out to the penny so your reports actually mean something." },
  { icon: Receipt, title: "IFTA Tracking & Quarterly Prep", body: "Monthly mileage and fuel tracking, and a prepared quarterly IFTA return, if you run Motive, Samsara, or another ELD that reports miles by state." },
  { icon: ClipboardCheck, title: "1099 & W-9 Tracking", body: "Contractor tracking and W-9 collection in the monthly engagement. E-file of 1099s is not in the base package." },
  { icon: Video, title: "Quarterly Video Reviews", body: "Recorded walkthroughs of your numbers on the 12-month plan — anytime in your portal." },
  { icon: ScanLine, title: "Receipt Digitization", body: "Snap, send, forget. We organize and tag every receipt against your books." },
  { icon: UserCheck, title: "Setup & Catch-Up", body: "New-client setup and catch-up bookkeeping. Three months of catch-up on the 6-month plan. Six months of catch-up on the 12-month plan." },
  { icon: MessagesSquare, title: "Flexible Communication", body: "Text, email, voice memo, or portal — synchronous when needed, async by default." },
];

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/11053643/pexels-photo-11053643.jpeg"
            alt="Cargo truck on highway at night"
            className="w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#040914] via-[#040914]/85 to-[#040914]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#040914] via-transparent to-[#040914]/40" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-20 sm:pt-28 pb-28 sm:pb-40">
          <motion.div initial="hidden" animate="show" variants={stagger} className="max-w-3xl">
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-[#FFCB05] animate-pulse" />
              <span className="text-xs font-semibold tracking-[0.18em] uppercase text-white/80">A boutique firm for the road</span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-white leading-[1.05]"
            >
              You know how to run a truck. <br />
              <span className="text-[#FFCB05]">We know how to run the books.</span>
            </motion.h1>

            <motion.p variants={fadeUp} className="mt-6 max-w-2xl text-base sm:text-lg text-[#9CA3AF] leading-relaxed">
              Bookkeeping and IFTA prep for owner-operators and small fleets. You stay on the road. We keep the numbers honest.
            </motion.p>

            <motion.div variants={fadeUp} className="mt-9 flex flex-wrap gap-3">
              <a
                href={LINKS.discoveryCall}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-book-call-btn"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold bg-[#FFCB05] text-[#040914] hover:bg-[#FFD633] transition-all hover:-translate-y-0.5"
              >
                See if Linehaul is a fit (30 min) <Calendar className="h-4 w-4" />
              </a>
              <Link
                to="/services"
                data-testid="hero-get-started-btn"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold border border-white/15 text-white hover:border-[#FFCB05]/50 hover:bg-white/5 transition-all"
              >
                See Services &amp; Pricing <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>

            <motion.div variants={fadeUp} className="mt-12 flex flex-wrap gap-x-8 gap-y-3 text-sm text-[#9CA3AF]">
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#FFCB05]" /> Built for OTR & owner-operators</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#FFCB05]" /> Midwest roots, U.S.-wide service</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#FFCB05]" /> QuickBooks Online certified</span>
            </motion.div>
          </motion.div>
        </div>

        {/* bottom hairline */}
        <div className="absolute bottom-0 inset-x-0 hairline" />
      </section>

      {/* VALUE PROP — fits your schedule */}
      <section className="relative py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#FFCB05]">Made for the road</p>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white leading-tight">
              We fit around <span className="text-[#FFCB05]">your</span> schedule.
            </h2>
            <p className="mt-5 text-base text-[#9CA3AF] leading-relaxed">
              You’re on the road. Loads don’t wait. Neither should your books. Send us a voice memo at 2am, a receipt from a truck stop, or a question between shifts — we’ll handle it on our end and circle back when you’re ready.
            </p>
            <div className="mt-8 flex gap-3">
              <Link
                to="/services"
                data-testid="value-explore-services-btn"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold border border-white/15 text-white hover:border-[#FFCB05]/50 hover:bg-white/5 transition-all"
              >
                Explore Services <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { icon: Zap, t: "Synchronous when needed", b: "Quick chat, video call, or screen share. Real-time when it matters." },
              { icon: Clock, t: "Asynchronous by default", b: "Send anything, anytime. We’ll respond within one business day." },
              { icon: MessagesSquare, t: "Flexible communication", b: "Text, email, voice memo, portal — meet you where you are." },
              { icon: ShieldCheck, t: "Quiet reliability", b: "Books done. Deadlines on the calendar. No drama. No surprises." },
            ].map((c, i) => (
              <motion.div
                key={c.t}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="group relative p-7 rounded-2xl bg-gradient-to-b from-[#00274C]/40 to-[#040914] border border-white/5 hover:border-[#FFCB05]/30 hover:-translate-y-1 transition-all duration-300"
              >
                <span className="inline-grid place-items-center h-10 w-10 rounded-xl bg-[#00274C] border border-white/10 group-hover:border-[#FFCB05]/40 transition-colors">
                  <c.icon className="h-5 w-5 text-[#FFCB05]" />
                </span>
                <h3 className="mt-5 font-display text-lg font-semibold text-white">{c.t}</h3>
                <p className="mt-2 text-sm text-[#9CA3AF] leading-relaxed">{c.b}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="relative py-24 sm:py-32 bg-[#0A111E] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#FFCB05]">What we do</p>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white leading-[1.1]">
                The stuff you hate. <span className="text-[#FFCB05]">We love it.</span>
              </h2>
              <p className="mt-4 text-base text-[#9CA3AF] leading-relaxed">
                Reconciling fuel receipts at 11pm? Chasing W-9s in January? Contractor payments that never got tracked? That’s our weekday. Here’s the full menu &mdash; bundled into our <Link to="/services" className="text-[#FFCB05] hover:underline">Specialized Bookkeeping</Link> engagement.
              </p>
            </div>
            <Link to="/services" data-testid="services-preview-cta" className="inline-flex items-center gap-2 text-sm font-semibold text-[#FFCB05] hover:text-[#FFD633] transition-colors">
              See services &amp; pricing <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {services.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="group relative p-7 rounded-2xl bg-gradient-to-b from-[#00274C]/50 to-[#040914] border border-white/5 hover:border-[#FFCB05]/30 hover:-translate-y-1 transition-all duration-300"
              >
                <span className="inline-grid place-items-center h-11 w-11 rounded-xl bg-[#040914] border border-white/10 group-hover:border-[#FFCB05]/40 transition-colors">
                  <s.icon className="h-5 w-5 text-[#FFCB05]" />
                </span>
                <h3 className="mt-6 font-display text-base font-semibold text-white">{s.title}</h3>
                <p className="mt-2 text-sm text-[#9CA3AF] leading-relaxed">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* INTEGRATIONS */}
      <section className="relative py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#FFCB05] text-center">Stack we run on</p>
          <h3 className="mt-3 text-center font-display text-2xl sm:text-3xl text-white">Modern tools. Real workflow.</h3>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { name: "QuickBooks Online", desc: "Certified, connected, automated.", href: LINKS.qbo, testid: "integration-qbo" },
              { name: "Financial Cents", desc: "Powers our secure client portal.", href: "/portal", testid: "integration-fc", internal: true },
              { name: "Calendly", desc: "Book us when it works for you.", href: LINKS.calendly, testid: "integration-calendly" },
            ].map((it) => {
              const Wrapper = it.internal ? Link : "a";
              const wrapperProps = it.internal
                ? { to: it.href }
                : { href: it.href, target: "_blank", rel: "noreferrer" };
              return (
                <Wrapper
                  key={it.name}
                  {...wrapperProps}
                  data-testid={it.testid}
                  className="group flex items-center justify-between p-6 rounded-2xl bg-[#0A111E] border border-white/5 hover:border-[#FFCB05]/30 transition-all hover:-translate-y-0.5"
                >
                  <div>
                    <p className="font-display text-base font-semibold text-white">{it.name}</p>
                    <p className="text-sm text-[#9CA3AF] mt-0.5">{it.desc}</p>
                  </div>
                  <ArrowUpRight className="h-5 w-5 text-[#9CA3AF] group-hover:text-[#FFCB05] transition-colors" />
                </Wrapper>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 sm:py-28">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#00274C] via-[#0A111E] to-[#040914] p-10 sm:p-16">
            <div className="absolute -top-24 -right-20 h-72 w-72 rounded-full bg-[#FFCB05]/15 blur-3xl" />
            <div className="relative">
              <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#FFCB05]">Ready when you are</p>
              <h3 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white max-w-2xl">
                Thirty minutes. No pitch deck.
              </h3>
              <p className="mt-4 max-w-xl text-[#9CA3AF]">
                We’ll walk through your trucks, your ELD, and where the books are today. If we’re a fit, you pick a term and we send the engagement letter.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={LINKS.discoveryCall}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="cta-calendly-btn"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold bg-[#FFCB05] text-[#040914] hover:bg-[#FFD633] transition-all hover:-translate-y-0.5"
                >
                  See if Linehaul is a fit (30 min) <Calendar className="h-4 w-4" />
                </a>
                <Link
                  to="/services"
                  data-testid="cta-get-started-btn"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold border border-white/15 text-white hover:border-[#FFCB05]/50 hover:bg-white/5 transition-all"
                >
                  See Services &amp; Pricing <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
